import React, { Component, Fragment } from 'react';
import axios from 'axios';

import {
  Modal,
  Button,
  Space,
  Table,
  Input,
  Form,
  Tag,
  Popconfirm,
  Tooltip,
  message,
} from 'antd';

import {
  FileTextOutlined,
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';

import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config/api';

// Order Entry templates: client-wide, named bundles of tests + diagnoses.
// Applying a template OVERWRITES the currently selected tests and diagnoses.
// Backend: /connect/templates (see backend routes/connect/templates.js).
class TemplatesModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clientID: props.client.id,
      clientCode: props.client.ClientCode,
      open: false,
      searchText: '',
      templates: [],
      loading: false,

      // save / edit form
      formOpen: false,
      editing: null, // template row being renamed, or null when creating
      formName: '',
      formDescription: '',
      saving: false,
    };
  }

  authHeaders = async () => {
    const token = await authService.ensureValidToken();
    if (!token) throw new Error('Not authenticated');
    return { Authorization: `Bearer ${token}` };
  };

  onClickModal = () => {
    this.setState(prev => ({ open: !prev.open, searchText: '', formOpen: false }), () => {
      if (this.state.open) this.loadTemplates();
    });
  };

  loadTemplates = async () => {
    this.setState({ loading: true });
    try {
      const headers = await this.authHeaders();
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/templates`,
        params: { clientID: this.state.clientID, q: this.state.searchText },
        headers,
      });
      this.setState({ templates: res.data.items || [], loading: false });
    } catch (err) {
      console.log(err);
      this.setState({ loading: false });
      message.error('Could not load templates');
    }
  };

  // ---------- apply ----------
  applyTemplate = async (record) => {
    const { onApplyTemplate, selectedTests = [], selectedDiagnoses = [] } = this.props;
    try {
      const headers = await this.authHeaders();
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/templates/${record.id}`,
        params: { clientID: this.state.clientID },
        headers,
      });
      const { tests = [], diagnoses = [] } = res.data;

      const doApply = () => {
        if (onApplyTemplate) onApplyTemplate({ tests, diagnoses });
        message.success(`Applied "${record.name}"`);
        this.setState({ open: false });
      };

      // Overwrite semantics: warn if the order already has selections.
      if (selectedTests.length || selectedDiagnoses.length) {
        Modal.confirm({
          title: `Apply "${record.name}"?`,
          content: 'This will replace the tests and diagnoses currently selected on this order.',
          okText: 'Replace',
          onOk: doApply,
        });
      } else {
        doApply();
      }
    } catch (err) {
      console.log(err);
      message.error('Could not load template');
    }
  };

  // ---------- create / rename ----------
  openCreateForm = () => {
    this.setState({ formOpen: true, editing: null, formName: '', formDescription: '' });
  };

  openEditForm = (record) => {
    this.setState({
      formOpen: true,
      editing: record,
      formName: record.name,
      formDescription: record.description || '',
    });
  };

  closeForm = () => this.setState({ formOpen: false, editing: null });

  submitForm = async () => {
    const { editing, formName, formDescription, clientID, clientCode } = this.state;
    const { selectedTests = [], selectedDiagnoses = [] } = this.props;
    const name = formName.trim();
    if (!name) return message.warning('Template name is required');

    const user = authService.getUserAttributes() || {};
    const createdBy = user.preferred_username || user.email || null;

    this.setState({ saving: true });
    try {
      const headers = await this.authHeaders();

      if (editing) {
        // Rename only. When "Update contents" was chosen the current selections
        // are sent instead, so the template's items get replaced too.
        const useCurrent = editing.__useCurrentSelections;
        const payload = {
          clientID,
          name,
          description: formDescription,
          tests: useCurrent ? selectedTests : editing.tests,
          diagnoses: useCurrent ? selectedDiagnoses : editing.diagnoses,
        };
        await axios({
          method: 'PUT',
          url: `${API_BASE_URL}/connect/templates/${editing.id}`,
          data: payload,
          headers,
        });
        message.success('Template updated');
      } else {
        if (!selectedTests.length && !selectedDiagnoses.length) {
          this.setState({ saving: false });
          return message.warning('Select at least one test or diagnosis on the order first');
        }
        await axios({
          method: 'POST',
          url: `${API_BASE_URL}/connect/templates`,
          data: {
            clientID,
            clientCode,
            name,
            description: formDescription,
            createdBy,
            tests: selectedTests,
            diagnoses: selectedDiagnoses,
          },
          headers,
        });
        message.success('Template saved');
      }

      this.setState({ saving: false, formOpen: false, editing: null });
      this.loadTemplates();
    } catch (err) {
      console.log(err);
      this.setState({ saving: false });
      const msg = err.response?.data?.error || 'Could not save template';
      message.error(msg);
    }
  };

  // Rename/edit needs the existing items so PUT can keep them.
  startEdit = async (record, useCurrentSelections = false) => {
    try {
      const headers = await this.authHeaders();
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/templates/${record.id}`,
        params: { clientID: this.state.clientID },
        headers,
      });
      this.openEditForm({ ...res.data, __useCurrentSelections: useCurrentSelections });
    } catch (err) {
      console.log(err);
      message.error('Could not load template');
    }
  };

  // ---------- delete ----------
  deleteTemplate = async (record) => {
    try {
      const headers = await this.authHeaders();
      await axios({
        method: 'DELETE',
        url: `${API_BASE_URL}/connect/templates/${record.id}`,
        data: { clientID: this.state.clientID },
        headers,
      });
      message.success('Template deleted');
      this.setState(prev => ({ templates: prev.templates.filter(t => t.id !== record.id) }));
    } catch (err) {
      console.log(err);
      message.error('Could not delete template');
    }
  };

  render() {
    const { open, templates, searchText, loading, formOpen, editing, formName, formDescription, saving } = this.state;
    const { selectedTests = [], selectedDiagnoses = [] } = this.props;
    const hasSelections = selectedTests.length > 0 || selectedDiagnoses.length > 0;

    const columns = [
      {
        title: 'Template',
        dataIndex: 'name',
        key: 'name',
        render: (name, record) => (
          <div>
            <strong>{name}</strong>
            {record.description ? <div style={{ color: '#888', fontSize: 12 }}>{record.description}</div> : null}
          </div>
        ),
      },
      {
        title: 'Contents',
        key: 'contents',
        width: 200,
        render: (_, record) => (
          <Space size={4}>
            <Tag color="blue">{record.testCount || 0} tests</Tag>
            <Tag color="purple">{record.diagnosisCount || 0} dx</Tag>
          </Space>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 210,
        align: 'right',
        render: (_, record) => (
          <Space size={4}>
            <Button type="primary" size="small" onClick={() => this.applyTemplate(record)}>
              <CheckOutlined /> Apply
            </Button>
            <Tooltip title="Rename">
              <Button size="small" onClick={() => this.startEdit(record)}>
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip title={hasSelections ? 'Replace contents with current order selections' : 'Select tests/diagnoses on the order first'}>
              <Button size="small" disabled={!hasSelections} onClick={() => this.startEdit(record, true)}>
                <SaveOutlined />
              </Button>
            </Tooltip>
            <Popconfirm
              title="Delete this template?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => this.deleteTemplate(record)}
            >
              <Button danger size="small">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <Fragment>
        <Space>
          <Button type="primary" ghost size="small" onClick={this.onClickModal}>
            <FileTextOutlined /> Templates
          </Button>
          <Tooltip title={hasSelections ? 'Save the selected tests and diagnoses as a template' : 'Select tests/diagnoses first'}>
            <Button
              size="small"
              disabled={!hasSelections}
              onClick={() => this.setState({ open: true }, () => { this.loadTemplates(); this.openCreateForm(); })}
            >
              <SaveOutlined /> Save as Template
            </Button>
          </Tooltip>
        </Space>

        <Modal
          title="Order Templates"
          centered
          open={open}
          onCancel={this.onClickModal}
          footer={null}
          width={800}
        >
          <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
            <Input.Search
              placeholder="Search templates"
              allowClear
              value={searchText}
              onChange={e => this.setState({ searchText: e.target.value })}
              onSearch={this.loadTemplates}
              style={{ width: 320 }}
            />
            <Button type="primary" disabled={!hasSelections} onClick={this.openCreateForm}>
              <SaveOutlined /> Save current selections as template
            </Button>
          </Space>

          {formOpen && (
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 12, background: '#fafafa' }}>
              <Form layout="vertical" size="small">
                <Form.Item label="Template name" required style={{ marginBottom: 8 }}>
                  <Input
                    value={formName}
                    maxLength={100}
                    autoFocus
                    onChange={e => this.setState({ formName: e.target.value })}
                    onPressEnter={this.submitForm}
                    placeholder="e.g. Annual Physical"
                  />
                </Form.Item>
                <Form.Item label="Description" style={{ marginBottom: 8 }}>
                  <Input
                    value={formDescription}
                    maxLength={255}
                    onChange={e => this.setState({ formDescription: e.target.value })}
                    placeholder="Optional"
                  />
                </Form.Item>
                {(!editing || editing.__useCurrentSelections) && (
                  <div style={{ marginBottom: 8, fontSize: 12, color: '#555' }}>
                    Will contain <strong>{selectedTests.length}</strong> test(s) and{' '}
                    <strong>{selectedDiagnoses.length}</strong> diagnosis code(s) from the current order.
                  </div>
                )}
                <Space>
                  <Button type="primary" loading={saving} onClick={this.submitForm}>
                    {editing ? 'Update' : 'Save'}
                  </Button>
                  <Button onClick={this.closeForm}>Cancel</Button>
                </Space>
              </Form>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={templates}
            loading={loading}
            size="small"
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'No templates yet. Select tests and diagnoses on the order, then save them as a template.' }}
            scroll={{ y: 400 }}
          />
        </Modal>
      </Fragment>
    );
  }
}

export default TemplatesModal;
